import { backendBaseUrl } from "src/domains/shared/api/calculation/backendBaseUrl";
import { getIdToken } from "src/domains/shared/utils/amplifyCompat";
import { HistoryPage } from "./projectHistory.model";

/**
 * projectHistory main-api 클라이언트 — Amplify 게이트웨이에 /v1 history 라우트가 없어
 * worksite client 와 동일한 raw fetch(`backendBaseUrl()` + `getIdToken()` Bearer) 패턴을
 * 도메인 내에 복제한다(worksite client.ts 헤더 주석의 도메인별 복제 관례 — design.md D2).
 * `backendBaseUrl()`(SSM DRONESQUARE_BACKEND)은 `/v1` 을 포함한다 — 스펙 경로
 * `/v1/projects/{id}/worksite/...` 를 worksite 가 `/projects/...` 상대 경로로 정상 호출 중.
 * 응답 봉투는 BRACKETED({status,data}) — 성공 응답을 unwrapBracketed 로 언랩해 소비한다.
 */

/** BRACKETED 성공 봉투. 에러 wire 는 { status: "Failed", message } + HTTP 에러 status. */
export interface BracketedResponse<T> {
  status: string;
  data: T;
}

/**
 * HTTP 에러 분기용 — 쿼리 retry 정책·UI 가 status(비멤버 404 등)로 안내 문구를 가른다.
 */
export class ProjectHistoryApiError extends Error {
  constructor(readonly status: number, message: string) {
    super(message);
    this.name = "ProjectHistoryApiError";
  }
}

/**
 * BRACKETED 봉투 언랩. HTTP 200 이어도 status 가 "Success" 가 아니면
 * 방어적으로 실패로 취급한다(에러 wire 는 원래 HTTP 에러 status 로 온다).
 */
function unwrapBracketed<T>(body: BracketedResponse<T>): T {
  if (body.status !== "Success") {
    throw new ProjectHistoryApiError(
      200,
      `unexpected envelope status: ${body.status}`
    );
  }
  return body.data;
}

/**
 * 인증 헤더(Bearer)를 자동으로 붙여 history 경로를 호출하고 봉투를 언랩한다.
 * !ok 응답은 wire 의 message 를 담은 ProjectHistoryApiError(HTTP status)로 던진다.
 */
async function requestWithAuth<T>(path: string): Promise<T> {
  const token = await getIdToken();
  const res = await fetch(`${backendBaseUrl()}${path}`, {
    headers: {
      "Content-Type": "application/json",
      Authorization: token,
    },
  });

  if (!res.ok) {
    let message = `project history API 요청 실패 (${res.status})`;
    try {
      const body = (await res.json()) as { message?: string };
      if (body?.message) message = body.message;
    } catch {
      // 에러 본문이 JSON 이 아니어도 status 기반 분기는 유지한다
    }
    throw new ProjectHistoryApiError(res.status, message);
  }

  return unwrapBracketed((await res.json()) as BracketedResponse<T>);
}

export interface FetchProjectHistoryParams {
  /** keyset cursor — 직전 응답의 next_cursor. null/undefined 면 최신부터. */
  cursor?: number | null;
  /** 페이지 크기 — 생략 시 서버 기본(20). */
  limit?: number | null;
  /** 이력 종류 필터 — null/undefined("전체")면 파라미터 자체를 생략한다. */
  historyType?: string | null;
}

/** 프로젝트 히스토리 타임라인 1페이지 조회 — 최신순(id 내림차순), 비멤버는 404. */
export const fetchProjectHistory = (
  projectId: number,
  { cursor, limit, historyType }: FetchProjectHistoryParams = {}
): Promise<HistoryPage> => {
  const search = new URLSearchParams();
  if (cursor != null) search.set("cursor", String(cursor));
  if (limit != null) search.set("limit", String(limit));
  if (historyType != null) search.set("history_type", historyType);

  const queryString = search.toString();
  return requestWithAuth<HistoryPage>(
    `/projects/${projectId}/history${queryString ? `?${queryString}` : ""}`
  );
};
